import { createContext, ReactNode, useContext, useMemo } from 'react';
import { usePhone, useTablet } from '@clds/style-utils';
import type { Account, ProductEnvironment, UnicornApi, User } from '@cld/unicorn-api';
import { SettingsAppContext } from '../settings.js';
import { useLocation } from 'wouter';

export interface AppContextType {
  userData: User;
  accountData: Account;
  currentCustomer: ProductEnvironment;
  refreshUserData: () => ReturnType<UnicornApi['methods']['notifyDataChanged']>;
  isMobile: boolean;
  unicornApi: UnicornApi;
  routingBasePath: string;
}

const appContext = createContext<AppContextType | undefined>(undefined);
export const AppContextProvider = appContext.Provider;

export const useAppContext = () => {
  const value = useContext(appContext);

  if (!value) {
    throw new Error('You need to wrap your component with AppContextConnected');
  }

  return value;
};

export const AppContextConnected = ({
  children,
  unicornApi,
}: {
  children?: ReactNode;
  unicornApi: UnicornApi;
}) => {
  const location = useLocation();
  const isPhone = usePhone();
  const isTablet = useTablet();
  const isMobile = isPhone || isTablet;
  const { productEnvironment } = unicornApi.data;

  const externalIdForUrl = `/c-${productEnvironment.externalId}`;
  const routingBasePath = `/settings${externalIdForUrl}`;

  const value = useMemo(
    () => ({
      userData: unicornApi.data.user,
      accountData: unicornApi.data.account,
      currentCustomer: unicornApi.data.productEnvironment,
      refreshUserData: () => unicornApi.methods.notifyDataChanged('user'),
      isMobile,
      unicornApi,
      routingBasePath,
    }),
    [isMobile, unicornApi, routingBasePath]
  );

  return (
    <AppContextProvider value={value}>
      <SettingsAppContext
        location={location}
        unicornApi={unicornApi}
        routingBasePath={routingBasePath}
      >
        {children}
      </SettingsAppContext>
    </AppContextProvider>
  );
};
